import { useMemo } from 'react';
import type { Asset, Site, Person, Category } from '@/types/models';
import type { DashboardFilterValues } from '../components/DashboardFilters';

interface DashboardMetrics {
  // Core counts
  totalAssets: number;
  filteredAssets: Asset[];

  // Value metrics
  totalValue: number;

  // Fiscal year metrics (current fiscal year - assumes July 1 start)
  purchasedThisFiscalYear: number;
  purchasedThisFiscalYearValue: number;

  // Depreciation metrics
  depreciableAssets: number;
  totalDepreciation: number;

  // By category breakdown
  assetsByCategory: { name: string; count: number; value: number }[];

  // By location (site) breakdown
  assetsBySite: { id: string; name: string; count: number; value: number }[];

  // By person breakdown
  assetsByPerson: { id: string; name: string; count: number; value: number }[];

  // Status breakdown
  assetsByStatus: { status: string; count: number }[];
}

/**
 * Get the start and end dates of the current fiscal year (July 1 - June 30)
 */
function getCurrentFiscalYear(): { start: Date; end: Date } {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed

  // Fiscal year starts July 1 (month index 6)
  // If current month is July or later (>= 6), fiscal year started this calendar year
  // If current month is before July (< 6), fiscal year started last calendar year
  const fiscalYearStart = currentMonth >= 6
    ? new Date(currentYear, 6, 1)  // July 1 of current year
    : new Date(currentYear - 1, 6, 1);  // July 1 of previous year

  const fiscalYearEnd = currentMonth >= 6
    ? new Date(currentYear + 1, 5, 30, 23, 59, 59)  // June 30 of next year
    : new Date(currentYear, 5, 30, 23, 59, 59);  // June 30 of current year

  return { start: fiscalYearStart, end: fiscalYearEnd };
}

/**
 * Calculate straight-line depreciation for an asset
 */
function calculateDepreciation(asset: Asset): number {
  if (!asset.is_depreciable || !asset.depreciable_cost || !asset.asset_life_months || !asset.date_acquired) {
    return 0;
  }

  const acquiredDate = new Date(asset.date_acquired);
  const now = new Date();
  const monthsOwned = Math.max(0,
    (now.getFullYear() - acquiredDate.getFullYear()) * 12 +
    (now.getMonth() - acquiredDate.getMonth())
  );

  // Limit to asset life
  const depreciableMonths = Math.min(monthsOwned, asset.asset_life_months);

  const salvageValue = asset.salvage_value || 0;
  const depreciableAmount = asset.depreciable_cost - salvageValue;
  const monthlyDepreciation = depreciableAmount / asset.asset_life_months;

  return depreciableMonths * monthlyDepreciation;
}

export function useDashboardMetrics(
  assets: Asset[],
  sites: Site[],
  people: Person[],
  categories: Category[],
  filters: DashboardFilterValues
): DashboardMetrics {
  return useMemo(() => {
    // Apply filters
    let filteredAssets = [...assets];

    if (filters.siteId) {
      filteredAssets = filteredAssets.filter(a => a.site_id === filters.siteId);
    }

    if (filters.personId) {
      filteredAssets = filteredAssets.filter(a =>
        a.assigned_to?.some(p => p.id === filters.personId)
      );
    }

    if (filters.categoryId) {
      filteredAssets = filteredAssets.filter(a => a.category_id === filters.categoryId);
    }

    // Core counts
    const totalAssets = filteredAssets.length;

    // Value metrics
    const totalValue = filteredAssets.reduce((sum, a) => sum + (a.cost || 0), 0);

    // Fiscal year metrics
    const { start: fiscalStart, end: fiscalEnd } = getCurrentFiscalYear();
    const fiscalYearAssets = filteredAssets.filter(a => {
      if (!a.purchase_date) return false;
      const purchaseDate = new Date(a.purchase_date);
      return purchaseDate >= fiscalStart && purchaseDate <= fiscalEnd;
    });
    const purchasedThisFiscalYear = fiscalYearAssets.length;
    const purchasedThisFiscalYearValue = fiscalYearAssets.reduce((sum, a) => sum + (a.cost || 0), 0);

    // Depreciation metrics
    const depreciableAssets = filteredAssets.filter(a => a.is_depreciable).length;
    const totalDepreciation = filteredAssets.reduce((sum, a) => sum + calculateDepreciation(a), 0);

    // By category breakdown
    const categoryMap = new Map<string, { name: string; count: number; value: number }>();
    categories.forEach(cat => {
      categoryMap.set(cat.id, { name: cat.name, count: 0, value: 0 });
    });
    categoryMap.set('uncategorized', { name: 'Uncategorized', count: 0, value: 0 });

    filteredAssets.forEach(asset => {
      const catId = asset.category_id || 'uncategorized';
      const entry = categoryMap.get(catId) || categoryMap.get('uncategorized')!;
      entry.count++;
      entry.value += asset.cost || 0;
    });

    const assetsByCategory = Array.from(categoryMap.values())
      .filter(c => c.count > 0)
      .sort((a, b) => b.count - a.count);

    // By site breakdown
    const siteMap = new Map<string, { id: string; name: string; count: number; value: number }>();
    sites.forEach(site => {
      siteMap.set(site.id, { id: site.id, name: site.name, count: 0, value: 0 });
    });
    siteMap.set('unassigned', { id: 'unassigned', name: 'No Location', count: 0, value: 0 });

    filteredAssets.forEach(asset => {
      const siteId = asset.site_id || 'unassigned';
      const entry = siteMap.get(siteId) || siteMap.get('unassigned')!;
      entry.count++;
      entry.value += asset.cost || 0;
    });

    const assetsBySite = Array.from(siteMap.values())
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count);

    // By person breakdown
    const personMap = new Map<string, { id: string; name: string; count: number; value: number }>();
    people.forEach(person => {
      personMap.set(person.id, { id: person.id, name: person.full_name, count: 0, value: 0 });
    });
    personMap.set('unassigned', { id: 'unassigned', name: 'Unassigned', count: 0, value: 0 });

    filteredAssets.forEach(asset => {
      if (asset.assigned_to && asset.assigned_to.length > 0) {
        asset.assigned_to.forEach(person => {
          const entry = personMap.get(person.id);
          if (entry) {
            entry.count++;
            entry.value += asset.cost || 0;
          }
        });
      } else {
        const entry = personMap.get('unassigned')!;
        entry.count++;
        entry.value += asset.cost || 0;
      }
    });

    const assetsByPerson = Array.from(personMap.values())
      .filter(p => p.count > 0)
      .sort((a, b) => b.count - a.count);

    // By status breakdown
    const statusMap = new Map<string, number>();
    filteredAssets.forEach(asset => {
      const count = statusMap.get(asset.status) || 0;
      statusMap.set(asset.status, count + 1);
    });

    const assetsByStatus = Array.from(statusMap.entries())
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalAssets,
      filteredAssets,
      totalValue,
      purchasedThisFiscalYear,
      purchasedThisFiscalYearValue,
      depreciableAssets,
      totalDepreciation,
      assetsByCategory,
      assetsBySite,
      assetsByPerson,
      assetsByStatus,
    };
  }, [assets, sites, people, categories, filters]);
}

export type { DashboardMetrics };
